// Redux/Slices/Common/Payment/PaymentSlice.js
import { createSlice } from "@reduxjs/toolkit";
import { axiosInstance } from "../../../../Common/Interceptors/AxiosConfig";
import { showNotification } from "../../../../Common/Toast/Toast";

const initialState = {
  isLoading: false,
  error: null,
  currentBooking: null,
  razorpayOrderId: null,
  paymentSuccess: false,
  bookingDetails: null,
};

const PaymentSlice = createSlice({
  name: "payment",
  initialState,
  reducers: {
    setErrors: (state, action) => {
      state.error= action.payload;
    },
    setLoading: (state, action) => {
      state.isLoading = action.payload;
    },
     setCurrentBooking: (state, action) => {
      state.currentBooking = action.payload;
    },
    setRazorpayOrderId: (state, action) => {
      state.razorpayOrderId = action.payload;
    },
    setPaymentSuccess: (state, action) => {
      state.paymentSuccess = action.payload;
    },
    setBookingDetails: (state, action) => {
      state.bookingDetails = action.payload;
    },
    resetPayment: (state) => {
      state.isLoading = false;
      state.error = null;
      state.currentBooking = null;
      state.razorpayOrderId = null;
      state.paymentSuccess = false;
      state.bookingDetails = null;
    },
    setOrderData: (state, action) => {
      state.orderData = action.payload;
    },
    setPaymentData: (state, action) => {
      state.paymentData = action.payload;
    },
    setRepairPaymentData: (state, action) => {
      state.repairPaymentData = action.payload;
    },
    setCashbackData: (state, action) => {
      state.cashbackData = action.payload;
    },
    setWithdrawalData: (state, action) => {
      state.withdrawalData = action.payload;
    },
    setPaymentStage: (state, action) => {
      state.paymentStage = action.payload;
    },
    setOrderStatus: (state, action) => {
      state.orderStatus = action.payload;
    },
    setVerificationStatus: (state, action) => {
      state.verificationStatus = action.payload;
    },
    setCashbackStatus: (state, action) => {
      state.cashbackStatus = action.payload;
    },
    setWithdrawalStatus: (state, action) => {
      state.withdrawalStatus = action.payload;
    },
    clearPaymentState: (state) => {
      state.orderData = null;
      state.paymentData = null;
    },
    clearCashbackState: (state) => {
      state.cashbackData = null;
    },
    clearErrors: (state) => {
      state.error = null;
    }
  }
});

// ✅ Fixed: Create repair order with proper notification format
export const createRepairOrder = (orderData) => async (dispatch) => {
  dispatch(setLoading(true));
  dispatch(setOrderStatus('creating'));
  dispatch(setPaymentStage('creating'));
  dispatch(clearErrors());
  
  try {
    const response = await axiosInstance.post("payment/repair", orderData);
    const { status, message, data } = response?.data || {};
    
    if (status === "success") {
      dispatch(setOrderData(data));
      dispatch(setPaymentStage('processing'));
      
      showNotification({
        message: "Repair order created successfully! Opening payment...",
        type: "success"
      });
      
      return { success: true, data };
    }
    
    throw new Error(message || "Repair order creation failed");
    
  } catch (error) {
    const errorMsg = error?.response?.data?.message || error.message;
    if (process.env.NODE_ENV === 'development') {
      
    }
    
    dispatch(setErrors(errorMsg));
    dispatch(setOrderStatus('failed'));
    dispatch(setPaymentStage('failed'));
    dispatch(setLoading(false));
    
    showNotification({
      message: errorMsg,
      type: "error"
    });
    
    return { success: false, error: errorMsg };
  }
};

// ✅ Fixed: Verify repair payment with proper notification format
export const verifyRepairPayment = (verificationData) => async (dispatch) => {
  dispatch(setLoading(true));
  dispatch(setVerificationStatus('verifying'));
  dispatch(setPaymentStage('verifying'));
  dispatch(clearErrors());
  
  try {
    const response = await axiosInstance.post("payment/repair/verify", verificationData);
    const { status, message, data } = response?.data || {};
    
    if (status === "success") {
      dispatch(setRepairPaymentData(data));
      dispatch(setLoading(false));
      
      showNotification({
        message: "Payment verified successfully! Service booked.",
        type: "success"
      });
      
      return { success: true, data };
    }
    
    throw new Error(message || "Payment verification failed");
    
  } catch (error) {
    
    const errorMsg = error?.response?.data?.message || error.message;
    
    dispatch(setErrors(errorMsg));
    dispatch(setVerificationStatus('failed'));
    dispatch(setPaymentStage('failed'));
    dispatch(setLoading(false));
    
    showNotification({
      message: errorMsg,
      type: "error"
    });
    
    return { success: false, error: errorMsg };
  }
};

// ✅ Fixed: Get user's repair payments
export const getMyRepairPayments = (filters = {}) => async (dispatch) => {
  dispatch(setLoading(true));
  dispatch(clearErrors());
  
  try {
    const params = new URLSearchParams(filters);
    const response = await axiosInstance.get(`payment/repair/my-payments?${params}`);
    const { status, message, data } = response?.data || {};
    
    if (status === "success") {
      dispatch(setLoading(false));
      return { success: true, data };
    }
    
    throw new Error(message || "Failed to fetch repair payments");
    
  } catch (error) {
    const errorMsg = error?.response?.data?.message || error.message;
    
    dispatch(setErrors(errorMsg));
    dispatch(setLoading(false));
    
    showNotification({
      message: errorMsg,
      type: "error"
    });
    
    return { success: false, error: errorMsg };
  }
};

// ✅ Fixed: Update repair payment status
export const updateRepairPaymentStatus = (paymentId, status) => async (dispatch) => {
  dispatch(setLoading(true));
  dispatch(clearErrors());
  
  try {
    const response = await axiosInstance.patch(`payment/repair/${paymentId}/status`, { status });
    const { status: responseStatus, message, data } = response?.data || {};
    
    if (responseStatus === "success") {
      dispatch(setLoading(false));
      
      showNotification({
        message: "Payment status updated successfully",
        type: "success"
      });
      
      return { success: true, data };
    }
    
    throw new Error(message || "Failed to update payment status");
    
  } catch (error) {
    const errorMsg = error?.response?.data?.message || error.message;
    
    dispatch(setErrors(errorMsg));
    dispatch(setLoading(false));
    
    showNotification({
      message: errorMsg,
      type: "error"
    });
    
    return { success: false, error: errorMsg };
  }
};

// ✅ Fixed: Get user cashback
export const getUserCashback = () => async (dispatch) => {
  dispatch(setLoading(true));
  dispatch(setCashbackStatus('loading'));
  dispatch(clearErrors());
  
  try {
    const response = await axiosInstance.get("payment/cashback");
    const { status, data } = response?.data || {};
    
    if (status === "success") {
      dispatch(setCashbackData(data));
      dispatch(setLoading(false));
      return { success: true, data };
    }
    
    throw new Error("Failed to fetch cashback data");
    
  } catch (error) {
    const errorMsg = error?.response?.data?.message || error.message || "Failed to fetch cashback";
    
    dispatch(setErrors(errorMsg));
    dispatch(setCashbackStatus('failed'));
    dispatch(setLoading(false));
    
    
    
    return { success: false, error: errorMsg };
  }
};

// ✅ Fixed: Withdraw cashback
export const withdrawCashback = (withdrawalData) => async (dispatch) => {
  dispatch(setLoading(true));
  dispatch(setWithdrawalStatus('processing'));
  dispatch(clearErrors());
  
  try {
    const response = await axiosInstance.post("payment/cashback/withdraw", withdrawalData);
    const { status, message, data } = response?.data || {};
    
    if (status === "success") {
      dispatch(setWithdrawalData(data));
      dispatch(setLoading(false));
      
      showNotification({
        message: "Withdrawal processed successfully",
        type: "success"
      });
      
      return { success: true, data };
    }
    
    throw new Error(message || "Withdrawal failed");
    
  } catch (error) {
    const errorMsg = error?.response?.data?.message || error.message;
    
    dispatch(setErrors(errorMsg));
    dispatch(setWithdrawalStatus('failed'));
    dispatch(setLoading(false));
    
    showNotification({
      message: errorMsg,
      type: "error"
    });
    
    throw error;
  }
};

export const { 
  setErrors, 
  setLoading, 
  setOrderData, 
  setPaymentData, 
  setRepairPaymentData,
  setCashbackData,
  setWithdrawalData,
  setPaymentStage,
  setOrderStatus,
  setVerificationStatus,
  setCashbackStatus,
  setWithdrawalStatus,
  clearPaymentState,
  clearCashbackState,
  clearErrors 
} = PaymentSlice.actions;

export default PaymentSlice.reducer;
